// Helpers to consistently extract message/success/data from our standard API
// response shape:
// public record ApiResponse(bool Success, string Message, object? Data = null, object? Errors = null);
// public record ApiResponse<T>(bool Success, string Message, T? Data = default, object? Errors = null);

export function getApiMessage(input: any): string | undefined {
  if (!input) return undefined;

  // If this is an axios error, prefer response.data first
  const res = (input && (input.response?.data ?? input.response)) ?? input;

  const candidates: Array<any> = [
    res,
    res?.data,
    res?.Data,
    // sometimes backends nest a second time
    res?.data?.data,
    res?.data?.Data,
  ];

  for (const obj of candidates) {
    if (!obj) continue;
    if (typeof obj === 'string') {
      const trimmed = obj.trim();
      if (trimmed) return trimmed;
      continue;
    }
    if (typeof obj !== 'object') continue;
    // Common casings
    if (typeof obj.message === 'string' && obj.message) return obj.message;
    if (typeof (obj as any).Message === 'string' && (obj as any).Message) return (obj as any).Message;
    // Other common fields
    if (typeof (obj as any).msg === 'string' && (obj as any).msg) return (obj as any).msg;
    if (typeof (obj as any).Msg === 'string' && (obj as any).Msg) return (obj as any).Msg;
    if (typeof (obj as any).error === 'string' && (obj as any).error) return (obj as any).error;
    if (typeof (obj as any).Error === 'string' && (obj as any).Error) return (obj as any).Error;
    if (typeof (obj as any).detail === 'string' && (obj as any).detail) return (obj as any).detail;
    if (typeof (obj as any).Detail === 'string' && (obj as any).Detail) return (obj as any).Detail;
    if (typeof (obj as any).title === 'string' && (obj as any).title) return (obj as any).title;
    if (typeof (obj as any).Title === 'string' && (obj as any).Title) return (obj as any).Title;
    if (typeof (obj as any).description === 'string' && (obj as any).description) return (obj as any).description;
    if (typeof (obj as any).Description === 'string' && (obj as any).Description) return (obj as any).Description;
    if (typeof (obj as any).reason === 'string' && (obj as any).reason) return (obj as any).reason;
    if (typeof (obj as any).statusMessage === 'string' && (obj as any).statusMessage) return (obj as any).statusMessage;
    // Sometimes APIs put message inside `error` or first element of `errors`
    if (obj.error && typeof obj.error.message === 'string' && obj.error.message) return obj.error.message;
    if (Array.isArray(obj.errors) && obj.errors.length) {
      const first = obj.errors[0];
      if (typeof first === 'string' && first) return first;
      if (first && typeof first.message === 'string' && first.message) return first.message;
      if (first && typeof first.Message === 'string' && first.Message) return first.Message;
    }
    // errors as object map: { field: ["msg1", "msg2"], other: "msg" }
    if (obj.errors && typeof obj.errors === 'object' && !Array.isArray(obj.errors)) {
      for (const key of Object.keys(obj.errors)) {
        const val = (obj.errors as any)[key];
        if (typeof val === 'string' && val) return val;
        if (Array.isArray(val) && val.length) {
          const first = val[0];
          if (typeof first === 'string' && first) return first;
          if (first && typeof first.message === 'string' && first.message) return first.message;
        }
      }
    }
  }
  // Fallbacks: status text from axios error
  if (typeof input?.response?.statusText === 'string' && input.response.statusText) return input.response.statusText;
  if (typeof input?.message === 'string' && input.message) return input.message;
  return undefined;
}

export function getApiSuccess(input: any): boolean | undefined {
  if (!input) return undefined;
  const res = (input && (input.response?.data ?? input.response)) ?? input;
  const obj = res?.data ?? res;
  // boolean
  if (typeof obj?.success === 'boolean') return obj.success;
  if (typeof obj?.Success === 'boolean') return obj.Success;
  // string/number flags
  const raw = (obj?.success ?? obj?.Success) as any;
  if (raw === 'true' || raw === '1' || raw === 1) return true;
  if (raw === 'false' || raw === '0' || raw === 0) return false;
  return undefined;
}

export function getApiData<T = any>(input: any): T | undefined {
  if (!input) return undefined;
  const res = (input && (input.response?.data ?? input.response)) ?? input;
  const obj = res?.data ?? res;
  if (obj && Object.prototype.hasOwnProperty.call(obj, 'data')) return obj.data as T;
  if (obj && Object.prototype.hasOwnProperty.call(obj, 'Data')) return (obj as any).Data as T;
  return undefined;
}

export default { getApiMessage, getApiSuccess, getApiData };
